/**
 * RAG 시스템 프론트엔드 애플리케이션의 메인 컴포넌트
 * 
 * 이 컴포넌트는 React Router를 사용하여 전체 애플리케이션의 라우팅 구조를 정의합니다.
 * 사용자 인터페이스는 다음 두 가지 주요 섹션으로 구성됩니다:
 * 1. 사용자 영역: 일반 사용자를 위한 채팅 인터페이스 제공
 * 2. 관리자 영역: 지식베이스 관리, 크롤링, 문서 업로드 등 시스템 관리 기능 제공
 * 
 * 라우팅에는 권한 검사 및 접근 제어 기능이 포함되어 있습니다.
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import AdminLoginModal from './components/AdminLoginModal';

// 레이아웃 컴포넌트
import MainLayout from './components/layouts/MainLayout';  // 메인 사용자 레이아웃
import AdminLayout from './components/layouts/AdminLayout';  // 관리자 레이아웃

// 사용자 페이지
import ChatPage from './pages/user/ChatPage';  // 챗봇 대화 페이지 (관리자용 원본 유지)
import UserChatPage from './pages/user/UserChatPage';  // 사용자용 채팅 페이지 (/user-chat)
import CasesPage from './pages/user/CasesPage';  // 적용 사례 페이지
import HomePage from './pages/HomePage';  // 메인 홈페이지

// 관리자 페이지
import CrawlPage from './pages/admin/CrawlPage';  // 웹 크롤링 관리 페이지
import DocumentsPage from './pages/admin/DocumentsPage';  // 문서 업로드/관리 페이지
import DatabasePage from './pages/admin/DatabasePage';  // 외부 DB 연동 페이지
import KnowledgeBasePage from './pages/admin/KnowledgeBasePage';  // 지식베이스 관리 페이지
import ModelsPage from './pages/admin/ModelsPage';  // 모델 설정 페이지
import ApiToolsPage from './pages/admin/ApiToolsPage';  // API 도구 관리 페이지
import AdminUsersPage from './pages/admin/AdminUsersPage';  // 관리자 계정 관리 페이지
import FaqPage from './pages/admin/FaqPage';  // 큐레이션된 답변 관리 페이지
import ChatHistoryPage from './pages/admin/ChatHistoryPage';  // 질의응답 내역 페이지
import ChatSessionsPage from './pages/admin/ChatSessionsPage';  // 대화 세션 조회 페이지
import DatabaseToolsPage from './pages/admin/DatabaseToolsPage';  // DB 도구 관리 페이지
import AdminSettingsPage from './pages/admin/AdminSettingsPage';  // 시스템 설정 페이지

// 투어 가이드 컴포넌트
import TourGuide from './components/TourGuide';

/**
 * App 컴포넌트 - 애플리케이션의 메인 진입점
 * 
 * 인증 상태와 권한을 관리하고, 이에 따라 접근 가능한 라우트를 제어합니다.
 * 현재는 개발 편의를 위해 인증과 관리자 권한이 기본적으로 부여됩니다.
 * 실제 배포 환경에서는 적절한 인증 및 권한 체계로 대체되어야 합니다.
 * 
 * @returns {JSX.Element} 라우팅이 설정된 전체 애플리케이션 UI
 */
const App = () => {
  // 인증 상태 관리
  const [isAuthenticated, setIsAuthenticated] = useState(true); // 일반 사용자는 항상 인증됨
  const [isAdmin, setIsAdmin] = useState(false); // 관리자는 로그인해야 함
  const [adminLoginModalOpen, setAdminLoginModalOpen] = useState(false);
  const [pendingAdminRoute, setPendingAdminRoute] = useState(null);
  const [isCheckingAdminToken, setIsCheckingAdminToken] = useState(true); // 토큰 확인 중인지 상태

  // 관리자 토큰 확인
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      // 토큰 유효성 검사
      fetch('/api/admin/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          setIsAdmin(true);
        } else {
          localStorage.removeItem('adminToken');
          setIsAdmin(false);
        }
        setIsCheckingAdminToken(false);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        setIsAdmin(false);
        setIsCheckingAdminToken(false);
      });
    } else {
      setIsCheckingAdminToken(false);
    }
  }, []);

  /**
   * 관리자 권한이 필요한 라우트를 위한 보호 컴포넌트
   * 관리자가 아닌 사용자는 로그인 모달을 표시합니다.
   * 
   * @param {object} props - 컴포넌트 속성
   * @param {JSX.Element} props.children - 보호할 컴포넌트/페이지
   * @returns {JSX.Element} 권한이 있으면 자식 컴포넌트, 없으면 로그인 모달
   */
  const AdminRoute = ({ children }) => {
    // 토큰 확인 중이면 로딩 상태 표시 (리다이렉트 방지)
    if (isCheckingAdminToken) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}>
          <Typography>관리자 권한 확인 중...</Typography>
        </Box>
      );
    }
    
    if (!isAdmin) {
      // 관리자 로그인이 필요함
      setAdminLoginModalOpen(true);
      return <Navigate to="/" />;
    }
    return children;
  };

  // 관리자 로그인 성공 처리
  const handleAdminLogin = (loginData) => {
    setIsAdmin(true);
    setAdminLoginModalOpen(false);
    setIsCheckingAdminToken(false); // 토큰 확인 완료
    
    // 페이지 새로고침 없이 상태만 업데이트하고 React Router로 이동
    const targetRoute = pendingAdminRoute || '/';
    
    // 약간의 딜레이를 두고 페이지 이동 (상태 업데이트 완료 보장)
    setTimeout(() => {
      window.location.href = targetRoute;
    }, 100);
    
    setPendingAdminRoute(null);
  };

  // 관리자 로그인 모달 닫기
  const handleAdminLoginClose = () => {
    setAdminLoginModalOpen(false);
    setPendingAdminRoute(null);
  };

  /**
   * 인증이 필요한 라우트를 위한 보호 컴포넌트
   * 인증되지 않은 사용자는 홈 페이지로 리다이렉트됩니다.
   * 
   * @param {object} props - 컴포넌트 속성
   * @param {JSX.Element} props.children - 보호할 컴포넌트/페이지
   * @returns {JSX.Element} 인증된 사용자면 자식 컴포넌트, 아니면 리다이렉트
   */
  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/" />;
  };

  return (
    // 전체 애플리케이션 컨테이너
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* 투어 가이드 컴포넌트 */}
      <TourGuide />
      
      <Routes>
        {/* 사용자 경로 - 일반 사용자용 UI */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="chat" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="user-chat" element={
            <ProtectedRoute>
              <UserChatPage />
            </ProtectedRoute>
          } />  {/* 사용자용 채팅 페이지 */}
          <Route path="cases" element={<CasesPage />} />
        </Route>

        {/* 관리자 경로 - 시스템 관리용 UI */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          {/* 관리자 홈 - 크롤링 페이지로 리다이렉트 */}
          <Route index element={<Navigate to="/admin/crawl" replace />} />
          <Route path="crawl" element={<CrawlPage />} />  {/* 웹 크롤링 관리 */}
          <Route path="documents" element={<DocumentsPage />} />  {/* 문서 관리 */}
          <Route path="database" element={<DatabasePage />} />  {/* DB 연동 */}
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />  {/* 지식베이스 관리 */}
          <Route path="faq" element={<FaqPage />} />  {/* 큐레이션된 답변 관리 */}
          <Route path="chat-history" element={<ChatHistoryPage />} />  {/* 질의응답 내역 */}
          <Route path="chat-sessions" element={<ChatSessionsPage />} />  {/* 대화 세션 조회 */}
          <Route path="models" element={<ModelsPage />} />  {/* 모델 설정 */}
          <Route path="api-tools" element={<ApiToolsPage />} />  {/* API 도구 관리 */}
          <Route path="database-tools" element={<DatabaseToolsPage />} />  {/* DB 도구 관리 */}
          <Route path="admin-users" element={<AdminUsersPage />} />  {/* 관리자 계정 관리 */}
          <Route path="settings" element={<AdminSettingsPage />} />  {/* 시스템 설정 */}
        </Route>

        {/* 404 페이지 - 존재하지 않는 경로는 홈으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* 관리자 로그인 모달 */}
      <AdminLoginModal
        open={adminLoginModalOpen}
        onClose={handleAdminLoginClose}
        onLogin={handleAdminLogin}
      />
    </Box>
  );
};

export default App; 